export const initialLearningState = {
  companyId: null,
  status: null,
  staffUserId: null,
  filters: {
    hours: null,
    status: 'held',
    skillName: '',
    integrationConnectionId: '',
    bookingAgentId: '',
    syntheticPreview: false,
  },
  items: [],
  nextCursor: null,
  history: {
    pending: false,
    loaded: false,
    slow: false,
    cancelled: false,
    retryMore: false,
    error: '',
  },
  recoveryErrors: {},
  details: null,
  action: null,
  preparation: null,
  resolutionUrl: null,
  pending: false,
  error: '',
  confirmed: false,
  syntheticPreview: false,
  needsStaffVerification: false,
  setupAttempts: {},
  recovery: null,
  recoveryResult: null,
  recoveryRequests: {},
  skillImportReplacement: null,
  skillImportResult: null,
  migrationForm: {
    file: null,
    reason: '',
    baselineImportOperationId: '',
    validated: false,
  },
  migrationRequest: null,
  migrationResult: null,
};

const learningReducer = (
  state = initialLearningState,
  { type, payload, companyId }
) => {
  if (
    companyId &&
    state.companyId &&
    companyId !== state.companyId &&
    type !== 'learning/ENTER'
  )
    return state;
  switch (type) {
    case 'learning/MIGRATION_FORM':
      return state.pending
        ? state
        : { ...state, migrationForm: { ...state.migrationForm, ...payload } };
    case 'learning/MIGRATION_REQUEST':
      return { ...state, migrationRequest: payload };
    case 'learning/CLEAR_MIGRATION':
      return state.pending
        ? state
        : { ...state, migrationRequest: null, action: null };
    case 'learning/IMPORT_SKILL':
      return {
        ...state,
        skillImportReplacement: null,
        skillImportResult: null,
      };
    case 'learning/CANCEL_SKILL_IMPORT':
      return { ...state, skillImportReplacement: null };
    case 'learning/SKILL_IMPORT_REPLACEMENT':
      return { ...state, pending: false, skillImportReplacement: payload };
    case 'learning/SKILL_IMPORTED':
      return {
        ...state,
        pending: false,
        skillImportReplacement: null,
        skillImportResult: payload.upload,
        status: {
          ...state.status,
          generation: payload.generation,
          learningEnabled: payload.learningEnabled,
        },
      };
    case 'learning/ENTER':
      return {
        ...initialLearningState,
        setupAttempts: state.setupAttempts,
        recoveryRequests: state.recoveryRequests,
        companyId: payload,
      };
    case 'learning/SIGNED_IN':
      return {
        ...state,
        staffUserId: payload.userId,
        needsStaffVerification: false,
        error: '',
        action: null,
        preparation: null,
        resolutionUrl: null,
        confirmed: false,
      };
    case 'learning/SIGNED_OUT':
      return {
        ...initialLearningState,
        setupAttempts: state.setupAttempts,
        recoveryRequests: state.recoveryRequests,
        companyId: state.companyId,
      };
    case 'learning/SETUP_STORED':
      return {
        ...state,
        setupAttempts: {
          ...state.setupAttempts,
          [payload.key]: {
            ...state.setupAttempts[payload.key],
            body: payload.body,
            recoveryError: false,
          },
        },
      };
    case 'learning/SETUP_STORAGE_ERROR':
      return {
        ...state,
        pending: false,
        setupAttempts: {
          ...state.setupAttempts,
          [payload.key]: {
            ...state.setupAttempts[payload.key],
            recoveryError: true,
          },
        },
      };
    case 'learning/SETUP_RESULT':
      return {
        ...state,
        pending: false,
        setupAttempts: {
          ...state.setupAttempts,
          [payload.key]: {
            ...state.setupAttempts[payload.key],
            result: payload.result,
          },
        },
      };
    case 'learning/END_VERIFICATION':
      return {
        ...state,
        staffUserId: null,
        needsStaffVerification: false,
        action: null,
        preparation: null,
        resolutionUrl: null,
      };
    case 'learning/PREPARE':
      return { ...state, preparation: null, resolutionUrl: null };
    case 'learning/FILTERS':
      return {
        ...state,
        recovery: null,
        recoveryResult: null,
        filters: {
          ...state.filters,
          ...payload,
          ...(payload.hours === null ? { status: 'held' } : {}),
        },
        items: [],
        nextCursor: null,
        details: null,
        history: initialLearningState.history,
        recoveryErrors: {},
        error: '',
        syntheticPreview: false,
        ...(payload.syntheticPreview !== undefined
          ? {
              action: null,
              preparation: null,
              resolutionUrl: null,
              confirmed: false,
            }
          : {}),
      };
    case 'learning/LOAD_HISTORY':
      return {
        ...state,
        history: {
          ...state.history,
          pending: true,
          retryMore: Boolean(payload && payload.more),
          slow: false,
          cancelled: false,
          error: '',
        },
      };
    case 'learning/HISTORY_SLOW':
      return state.history.pending
        ? { ...state, history: { ...state.history, slow: true } }
        : state;
    case 'learning/HISTORY_INTERRUPTED':
      return state.history.pending
        ? {
            ...state,
            pending: false,
            history: { ...state.history, pending: false, slow: false },
          }
        : state;
    case 'learning/CANCEL_HISTORY':
      if (!state.history.pending) return state;
      return {
        ...state,
        pending: false,
        history: { ...state.history, pending: false, slow: false, cancelled: true },
      };
    case 'learning/VIEW_DETAILS':
      return {
        ...state,
        details: { operationId: payload, pending: true, item: null, error: '' },
      };
    case 'learning/CLOSE_DETAILS':
      return { ...state, details: null };
    case 'learning/DETAILS_LOADED':
    case 'learning/DETAILS_FAILED':
      if (!state.details || state.details.operationId !== payload.operationId)
        return state;
      return {
        ...state,
        details: {
          ...state.details,
          pending: false,
          ...(type === 'learning/DETAILS_LOADED'
            ? { item: payload.item }
            : { error: payload.message }),
        },
      };
    case 'learning/STARTED':
      return {
        ...state,
        pending: true,
        history: { ...state.history, pending: Boolean(payload && payload.history) },
        error: '',
        confirmed: false,
      };
    case 'learning/STATUS_LOADED':
      return { ...state, status: payload };
    case 'learning/HISTORY_LOADED':
      if (
        payload.syntheticRequested !== undefined &&
        payload.syntheticRequested !== Boolean(state.filters.syntheticPreview)
      )
        return state;
      return {
        ...state,
        pending: false,
        history: {
          ...state.history,
          slow: false,
          cancelled: false,
          loaded: true,
          pending: false,
          error: '',
        },
        recoveryErrors: payload.append
          ? { ...state.recoveryErrors, ...payload.recoveryErrors }
          : payload.recoveryErrors || {},
        items: payload.append
          ? [
              ...state.items,
              ...payload.items.filter(
                (item) =>
                  !state.items.some(
                    (existing) => existing.operationId === item.operationId
                  )
              ),
            ]
          : payload.items,
        nextCursor: payload.nextCursor,
        syntheticPreview: payload.syntheticPreview === true,
      };
    case 'learning/ACTION_LOADED':
      return { ...state, pending: false, action: payload, confirmed: false };
    case 'learning/PREPARATION_CREATED':
      return {
        ...state,
        pending: false,
        preparation: payload,
        resolutionUrl: null,
      };
    case 'learning/RESOLUTION_OPENED':
      return { ...state, pending: false, resolutionUrl: payload.chatUrl };
    case 'learning/CONFIRMED':
      return {
        ...state,
        pending: false,
        confirmed: true,
        action: null,
        ...(state.action && state.action.payload._migrationSetup
          ? {
              migrationRequest: null,
              migrationResult: payload,
              migrationForm: {
                ...state.migrationForm,
                validated: false,
                ...(state.action.action === 'importMemory'
                  ? {
                      baselineImportOperationId: payload.result.operationId,
                    }
                  : {}),
              },
            }
          : {}),
      };
    case 'learning/RECOVERY_PREPARED':
      return {
        ...state,
        pending: false,
        recovery: payload,
        recoveryResult: null,
      };
    case 'learning/RECOVERY_REQUEST_STORED':
      return {
        ...state,
        recoveryRequests: {
          ...state.recoveryRequests,
          [payload.key]: payload.body,
        },
      };
    case 'learning/RECOVERY_CONFIRMED':
      return {
        ...state,
        pending: false,
        recovery: null,
        recoveryResult: payload,
      };
    case 'learning/READ_ACCESS_DENIED':
      return {
        ...state,
        pending: false,
        history: {
          ...initialLearningState.history,
          ...(payload.history ? { error: payload.message } : {}),
        },
        error: payload.history ? '' : payload.message,
        items: [],
        nextCursor: null,
        details: null,
        status: null,
        staffUserId: null,
        preparation: null,
        action: null,
        recovery: null,
        recoveryErrors: {},
        resolutionUrl: null,
      };
    case 'learning/ERROR':
      return {
        ...state,
        pending: false,
        history: {
          ...state.history,
          pending: false,
          ...(payload.history ? { error: payload.message } : {}),
        },
        error: payload.history ? '' : payload.message,
        recovery: null,
        action: null,
        resolutionUrl: null,
        ...(!payload.manualAction &&
        !payload.setupAction &&
        !payload.recoveryAction &&
        (payload.status === 401 || payload.status === 403)
          ? {
              items: [],
              nextCursor: null,
              preparation: null,
              status: null,
              details: null,
              history: initialLearningState.history,
            }
          : {}),
        ...(payload.manualAction &&
        (payload.status === 401 || payload.code === 'STAFF_REAUTH_REQUIRED')
          ? {
              staffUserId: null,
              preparation: null,
              needsStaffVerification: true,
            }
          : {}),
        ...(!payload.manualAction &&
        !payload.setupAction &&
        !payload.recoveryAction &&
        payload.status === 401
          ? {
              staffUserId: null,
              preparation: null,
              filters: initialLearningState.filters,
              items: [],
              nextCursor: null,
              syntheticPreview: false,
            }
          : {}),
      };
    default:
      return state;
  }
};

export default learningReducer;
